import { useState } from "react";
import { toast } from "sonner";
import { FileText, Upload, Download, Trash2, BadgeCheck, ScrollText } from "lucide-react";
import { api, errMsg, fmtTime, download } from "../lib/api";
import { usePoll } from "../lib/usePoll";
import { inputCls } from "./common";

const fmtSize = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const dl = (path, name) => download(path, name).catch((e) => toast.error(errMsg(e)));

export function OfficialRecord({ mid, code, minutes }) {
  const name = code || "meeting";
  return (
    <div className="sfap-card p-5 border-[#2D3B2D]" data-testid="official-record">
      <h3 className="font-serif font-bold flex items-center gap-2 mb-3"><ScrollText className="h-4 w-4 text-[#C85A32]" />Official meeting record</h3>
      <div className="grid gap-3 md:grid-cols-2">
        <div className="rounded-xl bg-[#FBF8F2] border border-[#E5DEC9] p-4">
          <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">Official attendance report</div>
          <button className="btn-sm btn-primary" onClick={() => dl(`/reports/meetings/${mid}/attendance.pdf`, `${name}-attendance.pdf`)} data-testid={`record-attendance-pdf-${mid}`}><Download className="h-4 w-4" />Attendance PDF</button>
        </div>
        <div className="rounded-xl bg-[#FBF8F2] border border-[#E5DEC9] p-4">
          <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">Approved minutes</div>
          {minutes.length ? minutes.map((d) => (
            <button key={d.id} className="btn-sm btn-ghost mr-2 mb-1" onClick={() => dl(`/documents/${d.id}/download`, d.original_filename)} data-testid={`record-minutes-${d.id}`}><BadgeCheck className="h-4 w-4 text-green-700" />{d.title}</button>
          )) : <span className="text-sm text-amber-800" data-testid={`record-no-minutes-${mid}`}>No approved minutes yet</span>}
        </div>
      </div>
    </div>
  );
}

export function MeetingDocuments({ mid, code, canEdit }) {
  const { data, reload } = usePoll(() => api.get(`/meetings/${mid}/documents`).then((r) => r.data), 0, [mid]);
  const [file, setFile] = useState(null);
  const [kind, setKind] = useState("agenda");
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const upload = async () => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("kind", kind);
    fd.append("title", title);
    setBusy(true);
    try {
      await api.post(`/meetings/${mid}/documents`, fd);
      toast.success("Document uploaded");
      setFile(null); setTitle("");
      document.getElementById("doc-file-input").value = "";
      reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const remove = async (d) => {
    const reason = window.prompt(`Remove "${d.title}"? Reason:`);
    if (reason === null) return;
    try { await api.delete(`/documents/${d.id}`, { params: { reason } }); toast.success("Document removed"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const approve = async (d, ok) => {
    const note = window.prompt(ok ? `Mark "${d.title}" as the approved minutes? Note (e.g. approved at the next meeting):` : "Reason for withdrawing approval:");
    if (note === null) return;
    try { await api.post(`/documents/${d.id}/approval`, { approve: ok, note }); toast.success(ok ? "Minutes marked approved" : "Approval withdrawn"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const groups = [["agenda", "Agenda"], ["minutes", "Minutes"], ["attachment", "Attachments"]];
  return (
    <div className="grid gap-5" data-testid="meeting-documents">
      {data && <OfficialRecord mid={mid} code={code} minutes={data.filter((d) => d.kind === "minutes" && d.approved)} />}
      {canEdit && (
        <div className="sfap-card p-5 flex flex-wrap items-end gap-3">
          <select className={`${inputCls} h-10 w-auto`} value={kind} onChange={(e) => setKind(e.target.value)} data-testid="doc-kind">
            <option value="agenda">Agenda</option><option value="minutes">Minutes</option><option value="attachment">Attachment</option>
          </select>
          <input className={`${inputCls} h-10 max-w-xs`} placeholder="Title (optional)" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="doc-title" />
          <input id="doc-file-input" type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png,.webp" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" data-testid="doc-file" />
          <button className="btn-sm btn-primary h-10" disabled={!file || busy} onClick={upload} data-testid="doc-upload-btn"><Upload className="h-4 w-4" />{busy ? "Uploading…" : "Upload"}</button>
          <p className="text-xs text-stone-500 w-full">PDF, Word, Excel, PowerPoint, text or images · max 20 MB · files are private and available only to signed-in parish staff and authorized monitors.</p>
        </div>
      )}
      {!data ? null : groups.map(([k, label]) => (
        <div key={k} className="sfap-card p-5">
          <h3 className="font-serif font-bold mb-2">{label}</h3>
          {data.filter((d) => d.kind === k).map((d) => (
            <div key={d.id} className="flex flex-wrap items-center justify-between gap-2 py-2 border-b border-[#F0EADC] last:border-0" data-testid={`doc-row-${d.id}`}>
              <span className="flex items-center gap-2 min-w-0"><FileText className="h-4 w-4 text-[#C85A32] shrink-0" /><span className="min-w-0"><b className="break-words">{d.title}</b>{d.approved && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-green-50 border border-green-300 text-green-800 text-[10px] font-bold uppercase px-2 py-0.5" data-testid={`doc-approved-badge-${d.id}`}><BadgeCheck className="h-3 w-3" />Approved</span>}<span className="block text-xs text-stone-500">{d.original_filename} · {fmtSize(d.size)} · {d.uploaded_by} · {fmtTime(d.created_at)}{d.approved ? ` · approved by ${d.approved_by} ${fmtTime(d.approved_at)}` : ""}</span></span></span>
              <span className="flex gap-2">
                <button className="btn-sm btn-ghost" onClick={() => download(`/documents/${d.id}/download`, d.original_filename).catch((e) => toast.error(errMsg(e)))} data-testid={`doc-download-${d.id}`}><Download className="h-4 w-4" />Download</button>
                {canEdit && d.kind === "minutes" && (d.approved
                  ? <button className="btn-sm btn-ghost" onClick={() => approve(d, false)} data-testid={`doc-withdraw-${d.id}`}>Withdraw approval</button>
                  : <button className="btn-sm bg-green-700 text-white" onClick={() => approve(d, true)} data-testid={`doc-approve-${d.id}`}><BadgeCheck className="h-4 w-4" />Mark approved</button>)}
                {canEdit && <button className="btn-sm btn-ghost" onClick={() => remove(d)} data-testid={`doc-remove-${d.id}`}><Trash2 className="h-4 w-4" /></button>}
              </span>
            </div>
          ))}
          {!data.filter((d) => d.kind === k).length && <p className="text-sm text-stone-400">None uploaded</p>}
        </div>
      ))}
    </div>
  );
}
